import React, { useMemo } from 'react';
import { View } from 'react-native';
import type { MatchBet, MatchPick } from '../../../src/friends/types/friends.types';
import { CorrectOutcomeRow } from '../../challenges/CorrectOutcomeRow';
import { getChallengeCardStyles } from '../../../src/challenges/styles/challengeCard.styles';
import { useTheme } from '../../../src/theme/useTheme';
import { formatDateShort, formatTimeOnly } from '../../../helpers/formatDateTime.helper';
import { MATCH_WINNER_MARKET_ID, MATCH_WINNER_MARKET_NAME } from '../../../src/challenges/constants/challenges.constants';
import { formatHandicapMarketDisplayName } from '../../../src/create/constants/matchDetails.constants';
import { getCorrectOutcomeLabel } from '../../../src/friends/helpers/matchActualResult.helper';
import { getMatchBetRowState } from '../../../src/friends/helpers/matchPickRowState.helper';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { MatchBetThreeColumnGrid } from '../../challenges/MatchBetThreeColumnGrid';
import { MatchBetThreeColumnMarketRow } from '../../challenges/MatchBetThreeColumnMarketRow';

/** Use isCorrectOutcome only for display; isExactScore is for points. */
function isPickCorrect(pick: MatchPick): boolean {
  return pick.isCorrectOutcome === true;
}
function isPickWrong(pick: MatchPick): boolean {
  return pick.isCorrectOutcome === false;
}

/** Throttle dev warning: log each matchId only once per session. */
const warnedUnsettledMatchIds = new Set<string>();

export interface FriendChallengeMatchRowsProps {
  matches: MatchBet[];
  /** Friend's display name (for context; pick row is still [You] | market | [Friend]) */
  friendName?: string;
  testID?: string;
}

export const FriendChallengeMatchRows: React.FC<FriendChallengeMatchRowsProps> = ({
  matches,
  friendName: _friendName,
  testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getChallengeCardStyles(theme), [theme]);
  const sortedMatches = useMemo(
    () => [...matches].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [matches]
  );

  return (
    <View style={styles.marketRowsContainer} testID={testID}>
      {sortedMatches.map((match) => {
        const state = getMatchBetRowState(match);
        const {
          isFinished,
          isLive,
          homeTeamName,
          awayTeamName,
          userPred,
          has1x2Picks,
          user1x2Pick,
          friend1x2Pick,
          otherPicksByMarket,
          user1x2Correct,
          user1x2Wrong,
          friend1x2Correct: friendCorrect,
          friend1x2Wrong: friendWrong,
          my1x2Label: myLabel,
          friend1x2Label: friendLabel,
          friend1x2Odds: friendOdds,
        } = state;

        if (__DEV__ && isFinished && (user1x2Pick?.isCorrectOutcome == null || friend1x2Pick?.isCorrectOutcome == null)) {
          if (!warnedUnsettledMatchIds.has(match.matchId)) {
            warnedUnsettledMatchIds.add(match.matchId);
            // eslint-disable-next-line no-console -- dev-only settlement debug
            console.warn('[1v1] Finished match but 1X2 pick unsettled (settlement may not have run)', {
              matchId: match.matchId,
            });
          }
        }

        const matchDatePart = formatDateShort(match.date);
        const matchTimePart = formatTimeOnly(match.date);
        const hasActualScore =
          match.matchResult.homeScore != null && match.matchResult.awayScore != null;
        const hasScore = isLive || (isFinished && hasActualScore);
        const displayHomeScore = hasActualScore
          ? (match.matchResult.homeScore ?? undefined)
          : isLive
            ? 0
            : undefined;
        const displayAwayScore = hasActualScore
          ? (match.matchResult.awayScore ?? undefined)
          : isLive
            ? 0
            : undefined;
        const homeKitConfig = getTeamKitConfig(match.homeTeam);
        const awayKitConfig = getTeamKitConfig(match.awayTeam);

        const correctOutcome1x2 =
          isFinished
            ? getCorrectOutcomeLabel(
                match.matchResult.homeScore,
                match.matchResult.awayScore,
                MATCH_WINNER_MARKET_ID,
                match.matchResult.halfTimeHomeScore,
                match.matchResult.halfTimeAwayScore
              )
            : null;

        return (
          <View key={match.matchId} style={styles.picksCard}>
            <MatchBetThreeColumnGrid
              styles={styles}
              showBetRow={has1x2Picks}
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
              homeKit={{
                primary: homeKitConfig.primary,
                secondary: homeKitConfig.secondary,
                type: homeKitConfig.type,
              }}
              awayKit={{
                primary: awayKitConfig.primary,
                secondary: awayKitConfig.secondary,
                type: awayKitConfig.type,
              }}
              kitSize={28}
              hasScore={hasScore}
              homeScore={displayHomeScore}
              awayScore={displayAwayScore}
              matchDatePart={matchDatePart}
              matchTimePart={matchTimePart}
              isLive={isLive}
              liveCaption={
                isLive && !hasScore ? 'LIVE' : undefined
              }
              centerMarketLabel={MATCH_WINNER_MARKET_NAME}
              centerBetFooter={
                has1x2Picks && isFinished && correctOutcome1x2 ? (
                  <CorrectOutcomeRow correctLabel={correctOutcome1x2} />
                ) : null
              }
              myPick={{
                label: myLabel,
                odds: userPred.odds ?? undefined,
                isCorrect: user1x2Correct,
                isWrong: user1x2Wrong,
                testID: testID ? `${testID}-my-1x2-${match.matchId}` : undefined,
              }}
              opponentPick={{
                label: friendLabel,
                odds: friendOdds,
                isCorrect: friendCorrect,
                isWrong: friendWrong,
                testID: testID ? `${testID}-friend-1x2-${match.matchId}` : undefined,
              }}
            />

            {otherPicksByMarket.length > 0 ? (
              <View style={styles.marketRowsContainer}>
                {otherPicksByMarket.map(([marketId, { marketName, user, friend }]) => {
                  const userLabel = user?.value?.trim() ? user.value : undefined;
                  const friendLabelOther = friend?.value?.trim() ? friend.value : undefined;
                  const friendOddsOther = friend?.odd;
                  const friendCorrectOther =
                    isFinished && friend != null && isPickCorrect(friend);
                  const friendWrongOther =
                    isFinished && friend != null && isPickWrong(friend);
                  const pickValue = user?.value ?? friend?.value;
                  const correctLabel = isFinished
                    ? getCorrectOutcomeLabel(
                        match.matchResult.homeScore,
                        match.matchResult.awayScore,
                        marketId,
                        match.matchResult.halfTimeHomeScore,
                        match.matchResult.halfTimeAwayScore,
                        pickValue
                      )
                    : '';
                  return (
                    <MatchBetThreeColumnMarketRow
                      key={marketId}
                      styles={styles}
                      marketNameText={formatHandicapMarketDisplayName(marketId, marketName, user?.value ?? friend?.value)}
                      centerFooter={correctLabel ? <CorrectOutcomeRow correctLabel={correctLabel} /> : null}
                      myPick={{
                        label: userLabel,
                        odds: user?.odd,
                        isCorrect: user != null && isPickCorrect(user),
                        isWrong: user != null && isPickWrong(user),
                        testID: testID ? `${testID}-my-${match.matchId}-m${marketId}` : undefined,
                      }}
                      opponentPick={{
                        label: friendLabelOther,
                        odds: friendOddsOther,
                        isCorrect: friendCorrectOther,
                        isWrong: friendWrongOther,
                        testID: testID ? `${testID}-friend-${match.matchId}-m${marketId}` : undefined,
                      }}
                    />
                  );
                })}
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );
};
